// Ways into the guided tours: the "? Tours" menu in the header (beginner tour of the whole site,
// advanced editor tour), a one-time welcome prompt for first visitors on the River, and the
// "Editor tour" button in the editor.
import { useEffect, useRef, useState } from "react";
import { usePath } from "../lib/router";
import { useConsent } from "../ui/consent";
import { useTour, type TourId } from "./store";
import { TOUR_NAMES } from "./tours";

export function TourMenu() {
  const start = useTour((s) => s.start);
  const ref = useRef<HTMLDetailsElement>(null);
  const pick = (t: TourId) => {
    if (ref.current) ref.current.open = false;
    start(t);
  };
  // Close when clicking elsewhere.
  useEffect(() => {
    const close = (e: PointerEvent) => {
      if (ref.current?.open && !ref.current.contains(e.target as Node)) ref.current.open = false;
    };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, []);
  return (
    <details className="tour-menu" ref={ref} data-testid="tour-menu">
      <summary className="btn btn-small btn-ghost" aria-label="Guided tours" title="Guided tours">
        <span aria-hidden="true">?</span> Tours
      </summary>
      <div className="tour-menu-list" role="menu">
        <button type="button" role="menuitem" onClick={() => pick("site")} data-testid="tour-start-site">
          <strong>{TOUR_NAMES.site}</strong>
          <span>The whole site in a minute</span>
        </button>
        <button type="button" role="menuitem" onClick={() => pick("editor")} data-testid="tour-start-editor">
          <strong>{TOUR_NAMES.editor}</strong>
          <span>Every tool for writing songs</span>
        </button>
      </div>
    </details>
  );
}

/** First visit on the River (after the cookie choice): offer the beginner tour once. */
export function TourWelcome() {
  const path = usePath();
  const seen = useTour((s) => s.seen.site);
  const running = useTour((s) => s.tour !== null);
  const decided = useConsent((s) => s.choice !== null);
  const [later, setLater] = useState(false);
  if (path !== "/" || seen || running || !decided || later) return null;
  return (
    <aside className="tour-welcome panel" aria-label="Guided tour" data-testid="tour-welcome">
      <p>
        <strong>New here?</strong> Take the one-minute tour of The Infinite.
      </p>
      <div className="row">
        <button
          type="button"
          className="btn btn-small btn-primary"
          onClick={() => useTour.getState().start("site")}
          data-testid="tour-welcome-start"
        >
          Show me around
        </button>
        <button
          type="button"
          className="btn btn-small btn-ghost"
          onClick={() => {
            setLater(true);
            useTour.getState().dismiss("site");
          }}
          data-testid="tour-welcome-dismiss"
        >
          Not now
        </button>
      </div>
    </aside>
  );
}

/** The editor's own button for the advanced tour. */
export function EditorTourButton() {
  return (
    <button
      type="button"
      className="btn btn-small btn-ghost editor-tour-btn"
      onClick={() => useTour.getState().start("editor")}
      title="A guided tour of every tool in the editor"
      data-testid="editor-tour"
    >
      🎓 Editor tour
    </button>
  );
}

/** The tours on the account settings page: start either one, see which you've taken. */
export function TourSettings() {
  const seen = useTour((s) => s.seen);
  const start = useTour((s) => s.start);
  const tours: { id: TourId; text: string }[] = [
    { id: "site", text: "The whole site in about a minute: the stream, the calendar, writing, tapes and more." },
    {
      id: "editor",
      text: "Every tool of the editor: recording, patterns, lanes, mixing, the stereo stage and the Wizard.",
    },
  ];
  return (
    <section className="panel stack" aria-labelledby="tours-h" data-testid="account-tours">
      <h2 id="tours-h">Guided tours</h2>
      <p>Take a tour again any time — they're also in the “? Tours” menu at the top of every page.</p>
      <ul className="tour-settings">
        {tours.map(({ id, text }) => (
          <li key={id}>
            <div>
              <strong>{TOUR_NAMES[id]}</strong>
              <span className="muted small">{seen[id] ? " · taken" : ""}</span>
              <p className="muted small">{text}</p>
            </div>
            <button
              type="button"
              className="btn btn-small"
              onClick={() => start(id)}
              data-testid={`account-tour-${id}`}
            >
              {seen[id] ? "Take again" : "Start"}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
